/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useEffect, useState } from "react";
import { Box, CardHeader, CircularProgress } from "@mui/material";
import Typography from "@mui/material/Typography";
import styled from "@emotion/styled";
import {
  getMonitorStatusSplitForProjectOrSystem,
  MonitorStatusSplit,
} from "../../api/cirrommonitor";
import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import "chart.js/auto";
import { Doughnut } from "react-chartjs-2";
import { amber, blue, indigo } from "@mui/material/colors";
import { NUMBER_FORMATTER } from "../utils/utils";

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
  justify-content: center;
`;

const LegendItem = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: ${({ isActive }) => (isActive ? 1 : 0.5)};
  font-size: 0.75rem;
`;

const LegendColorBox = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  background-color: ${({ color }) => color};
  border-radius: 2px;
  flex-shrink: 0;
`;

export const StatusSplitChart: React.FC<{
  isDataProject: boolean;
  integratedSystemId: string;
}> = ({ isDataProject, integratedSystemId }) => {
  const [statusSplit, setStatusSplit] = useState<MonitorStatusSplit | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (integratedSystemId) {
      setIsLoading(true);
      getMonitorStatusSplitForProjectOrSystem(
        isDataProject ? Number.parseInt(integratedSystemId) : undefined,
        !isDataProject ? Number.parseInt(integratedSystemId) : undefined
      )
        .then((response) => {
          setStatusSplit(response);
        })
        .finally(() => setIsLoading(false));
    }
  }, [integratedSystemId, isDataProject]);

  const categories = [
    { label: "Matched", key: "matched", color: indigo[500] },
    { label: "Suggested", key: "suggested", color: amber[500] },
    { label: "Monitored", key: "monitored", color: blue[500] },
  ];

  const createChartData = () => {
    if (!statusSplit) return { datasets: [] };
    const data = categories.map((cat) => statusSplit[cat.key] || 0);
    const colors = categories.map((cat) => cat.color);
    return {
      labels: categories.map((cat) => cat.label),
      datasets: [
        {
          data,
          backgroundColor: colors,
          borderWidth: 2,
          borderColor: "#fff",
        },
      ],
    };
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce(
              (acc: number, val: number) => acc + val,
              0
            );
            const percentage =
              total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label}: ${NUMBER_FORMATTER.format(
              value
            )} (${percentage}%)`;
          },
        },
      },
    },
    cutout: "70%",
  };

  const total = statusSplit
    ? statusSplit.matched + statusSplit.suggested + statusSplit.monitored
    : 0;

  if (isLoading) {
    return (
      <MiraCard style={{ height: "100%" }}>
        <CardHeader title="Contact Status Distribution" />
        <MiraCardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
          >
            <CircularProgress />
          </Box>
        </MiraCardContent>
      </MiraCard>
    );
  }

  return (
    <MiraCard style={{ height: "100%" }}>
      <CardHeader
        title="Contact Status Distribution"
        subheader="Breakdown of contact matching status"
      />
      <MiraCardContent>
        <Box
          style={{
            height: "250px",
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Doughnut data={createChartData()} options={chartOptions} />
          <Box
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
            }}
          >
            <Typography
              variant="h3"
              style={{ fontWeight: "bold", lineHeight: 1 }}
            >
              {NUMBER_FORMATTER.format(total)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Total Contacts
            </Typography>
          </Box>
        </Box>

        <LegendContainer>
          {categories.map((cat, index) => {
            const value = statusSplit?.[cat.key] || 0;
            const percentage =
              total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";

            return (
              <LegendItem key={index} isActive={value > 0}>
                <LegendColorBox color={cat.color} />
                <span style={{ fontWeight: value > 0 ? "bold" : "normal" }}>
                  {cat.label}: {NUMBER_FORMATTER.format(value)} ({percentage}%)
                </span>
              </LegendItem>
            );
          })}
        </LegendContainer>
      </MiraCardContent>
    </MiraCard>
  );
};
